import { create } from 'zustand';

import type { ImpersonationSession } from '@/entities/tenant';

const STORAGE_KEY = 'smartkeur:impersonation';

/**
 * Impersonatie is een toestand die je nooit mag vergeten.
 *
 * Daarom staat hij in `sessionStorage` en niet alleen in het geheugen: na een
 * herlaad van de pagina moet de balk er nog steeds staan. `sessionStorage` en
 * niet `localStorage`, want de sessie hoort bij dit tabblad en bij dit tijdelijke
 * token - hem stilletjes laten terugkomen in een nieuw venster zou precies het
 * verkeerde zijn.
 *
 * Deze store houdt de zichtbare status bij. Het token zelf gaat naar
 * `useSessionStore`: `startImpersonationBridge` in `src/app/providers` spiegelt
 * elke wijziging daarheen, zodat de http-client er ook mee praat.
 */
export interface ImpersonationState {
    session: ImpersonationSession | null;
    start: (session: ImpersonationSession) => void;
    stop: () => void;
}

function isSession(value: unknown): value is ImpersonationSession {
    if (value === null || typeof value !== 'object') {
        return false;
    }

    const record = value as Record<string, unknown>;

    return ['sessionUuid', 'token', 'tenantUuid', 'tenantName', 'userName'].every(
        (key) => typeof record[key] === 'string',
    );
}

function read(): ImpersonationSession | null {
    try {
        const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;

        // Een sessie in een oudere vorm kan niet meer gestopt worden; weg ermee.
        return isSession(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function write(session: ImpersonationSession | null): void {
    try {
        if (session) {
            globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(session));
        } else {
            globalThis.sessionStorage?.removeItem(STORAGE_KEY);
        }
    } catch {
        /* Privémodus of geblokkeerde opslag: de balk blijft in het geheugen staan. */
    }
}

export const useImpersonationStore = create<ImpersonationState>((set) => ({
    session: read(),
    start(session) {
        write(session);
        set({ session });
    },
    stop() {
        write(null);
        set({ session: null });
    },
}));
